// THE BOOK'S STRUCTURE — which page is print page 1, and which side every
// album page sits on (design system v2 §7 S1, owner 2026-10-14).
//
// ⚠️ OWNER, 2026-10-14 (#MAN-510613, « exactly like we had before »): the intro
// page is ALWAYS printed and album page 1 is ALWAYS a LEFT page — themed books
// included. `THEMED_OPENING_ALONE = false` folds the THEMED kind below into the
// PLAIN one everywhere: `isThemedBook` answers false and every function treats
// `themed` as false. The S1 opening page stays — it is simply album page 1, on
// the left, after the intro. Set it back to true to restore the opening-alone
// book; nothing else needs to change.
//
// ─── TWO KINDS OF BOOK ──────────────────────────────────────────────────────
//
//   PLAIN (every album until now — must stay byte-identical):
//     print 1 = the Storiz intro page (a right page, app_settings.intro_page_1_url)
//     album page 1 = print page 2 (LEFT), album page 2 = print page 3 (right)…
//     spreads = album pages (1, 2), (3, 4)…
//
//   THEMED (a theme built it and its first page is the S1 opening):
//     the separate intro page is NOT printed — S1 carries the small Storiz mark;
//     album page 1 = S1 = print page 1, a RIGHT page standing alone;
//     album page N = print page N; even pages are LEFT, odd pages RIGHT;
//     spreads = album pages (2, 3), (4, 5)…
//
// The book says which kind it is by itself: its first page is a themed S1
// page (`theme` set, `model` 'S1'). No flag to keep in step, no migration — and
// a customer who deletes or moves the opening page simply gets the plain book
// (intro page back, album page 1 on the left).
//
// The outro and the ÷ 4 padding are the same for both kinds.
//
// SPREADS — what lies open side by side, in page-ARRAY indexes (album page N
// is index N − 1). Every screen that shows the book two pages at a time (the
// editor, its strip, the flow, the page checker) pairs pages with
// `spreadCount` / `spreadPages` / `spreadOf` — never `spread * 2` by hand:
//   plain   spread s = indexes (2s, 2s + 1)      → (0, 1), (2, 3)…
//   themed  spread s = indexes (2s − 1, 2s)      → (—, 0), (1, 2), (3, 4)…
//
// ⚠️ ONE FILE, SHIPPED TWICE: web/src/lib/bookStructure.ts and
// worker/src/lib/bookStructure.ts are byte-identical (bookStructure.shared.test.ts).
//
// Pure. No imports.

/** The model code of the opening page (catalog/specials.ts). */
export const OPENING_MODEL = 'S1';

export interface BookPageLike { theme?: unknown; model?: unknown }

/** Does a themed book open on its S1 page ALONE (no intro, page 1 on the right)?
 *  false since 2026-10-14 — see the header. */
export const THEMED_OPENING_ALONE = false;

/** True when the album's first page is the themed S1 opening — the CONTENT
 *  fact, whatever the book's structure. */
export function opensOnS1(pages: ReadonlyArray<BookPageLike | null | undefined> | null | undefined): boolean {
  const p = pages && pages.length ? pages[0] : null;
  return !!p && typeof p.theme === 'string' && p.theme.length > 0 && p.model === OPENING_MODEL;
}

/** True when the book is laid out as the THEMED kind (S1 alone on print page 1,
 *  no intro). Always false while THEMED_OPENING_ALONE is false. */
export function isThemedBook(pages: ReadonlyArray<BookPageLike | null | undefined> | null | undefined): boolean {
  return THEMED_OPENING_ALONE && opensOnS1(pages);
}

/** The structure flag a caller's own `themed` really means. */
const alone = (themed: boolean): boolean => THEMED_OPENING_ALONE && !!themed;

/**
 * Pages printed BEFORE album page 1: the Storiz intro page, when the brand has
 * one and the book is not themed. 0 or 1.
 */
export function bookLeadPages(themed: boolean, hasIntroAsset: boolean): number {
  return !alone(themed) && hasIntroAsset ? 1 : 0;
}

/** Is the intro page printed for this book? */
export function printsIntro(themed: boolean, hasIntroAsset: boolean): boolean {
  return bookLeadPages(themed, hasIntroAsset) === 1;
}

/**
 * The index to hand `baseFrameMm` for album page `pageIndex` (0-based), so the
 * 15 mm spine margin lands on the binding side. baseFrameMm treats even indexes
 * as LEFT pages (the plain book). In a themed book album page 1 is a RIGHT page,
 * so every page shifts by one.
 */
export function spineIndex(pageIndex: number, themed: boolean): number {
  return alone(themed) ? pageIndex + 1 : pageIndex;
}

/** True when album page `albumPage` (1-based) is a left-hand page of the printed book. */
export function isLeftPage(albumPage: number, themed: boolean): boolean {
  const n = Math.max(1, Math.round(albumPage));
  return alone(themed) ? n % 2 === 0 : n % 2 === 1;
}

/**
 * The album page facing `albumPage` (1-based) on its spread, or null when it
 * stands alone (a themed book's opening page, or a last page with nothing
 * after it in `total` album pages).
 */
export function facingPage(albumPage: number, themed: boolean, total: number): number | null {
  const n = Math.max(1, Math.round(albumPage));
  if (alone(themed) && n === 1) return null;
  const mate = isLeftPage(n, themed) ? n + 1 : n - 1;
  return mate >= 1 && mate <= total ? mate : null;
}

/**
 * The page-array index of the LEFT side of spread `spread` — may be −1 (the
 * empty left side facing a themed book's opening page) or past the end.
 */
export function spreadFirstIndex(spread: number, themed: boolean): number {
  return 2 * Math.max(0, Math.floor(spread)) - (alone(themed) ? 1 : 0);
}

/** How many spreads an album of `total` pages opens as (0 with no pages). */
export function spreadCount(total: number, themed: boolean): number {
  const n = Math.max(0, Math.floor(total));
  return n === 0 ? 0 : Math.ceil((n + (alone(themed) ? 1 : 0)) / 2);
}

/**
 * The page-array indexes `[left, right]` open on spread `spread` of an album of
 * `total` pages; null = no page on that side (a themed book's opening stands
 * alone on the right; an odd last page stands alone).
 */
export function spreadPages(spread: number, themed: boolean, total: number): [number | null, number | null] {
  const left = spreadFirstIndex(spread, themed);
  const at = (i: number): number | null => (i >= 0 && i < total ? i : null);
  return [at(left), at(left + 1)];
}

/** The spread on which the page at array index `index` lies open. */
export function spreadOf(index: number, themed: boolean): number {
  return Math.floor((Math.max(0, Math.floor(index)) + (alone(themed) ? 1 : 0)) / 2);
}
